import { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getConfluenceZones, ConfluenceZone } from '../api/confluenceZones';
import StandaloneSection from './shared/StandaloneSection';
import { useGlobalAutoRefreshStore, useAutoRefreshTrigger } from '../stores/useGlobalAutoRefreshStore';
import { getPivotFetchRange, PivotTimeframe } from '../utils/pivotDateRanges';
import './ConfluenceZoneCard.css';

interface ConfluenceZoneCardProps {
  productId: string;
  startDate?: Date;
  endDate?: Date;
  pivotTimeframe?: string;
}

export function ConfluenceZoneCard({ productId, startDate, endDate, pivotTimeframe }: ConfluenceZoneCardProps) {
  const queryClient = useQueryClient();
  const setActiveDashboard = useGlobalAutoRefreshStore((state) => state.setActiveDashboard);
  const refreshTrigger = useAutoRefreshTrigger('market-overview');

  // Use the pivotTimeframe prop directly - parent component controls this
  const selectedTimeframe = (pivotTimeframe || 'TWENTY_FOUR_HOUR') as PivotTimeframe;

  // Calculate date range - confluence zones are calculated from pivot points,
  // so they should use the same date range as pivot fetches
  const dateRange = useMemo(() =>
    getPivotFetchRange(selectedTimeframe, startDate, endDate),
    [selectedTimeframe, startDate, endDate]
  );

  useEffect(() => {
    setActiveDashboard('market-overview');
    return () => setActiveDashboard(null);
  }, [setActiveDashboard]);

  useEffect(() => {
    if (refreshTrigger > 0) {
      queryClient.invalidateQueries({ queryKey: ['confluence-zones', productId, dateRange.start, dateRange.end, selectedTimeframe] });
    }
  }, [refreshTrigger, queryClient, productId, dateRange, selectedTimeframe]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['confluence-zones', productId, dateRange.start, dateRange.end, selectedTimeframe],
    queryFn: () => {
      return getConfluenceZones({
        productId,
        startDate: dateRange.start,
        endDate: dateRange.end,
        pivotTimeframe: selectedTimeframe,
        priceRangePct: 0.05,
        minLevels: 2
      });
    },
    retry: 2,
    refetchInterval: 60000,
  });

  const topZone = useMemo(() => {
    if (!data || !data.confluenceZones || data.confluenceZones.length === 0) {
      return null;
    }
    return data.confluenceZones[0];
  }, [data]);

  const getStrengthBadge = (zone: ConfluenceZone) => {
    if (zone.confluenceCount >= 7) {
      return <span className="strength-badge critical">CRITICAL</span>;
    } else if (zone.confluenceCount >= 5) {
      return <span className="strength-badge strong">STRONG</span>;
    } else if (zone.confluenceCount >= 3) {
      return <span className="strength-badge moderate">MODERATE</span>;
    }
    return <span className="strength-badge weak">WEAK</span>;
  };

  const isNearPrice = (zone: ConfluenceZone) => {
    return Math.abs(zone.distancePct) <= 0.5;
  };

  if (isLoading) {
    return (
      <StandaloneSection title="CONFLUENCE ZONE">
        <div className="loading-state">Loading confluence zone data...</div>
      </StandaloneSection>
    );
  }

  if (isError) {
    return (
      <StandaloneSection title="CONFLUENCE ZONE">
        <div className="error-state">Error loading confluence zone data</div>
      </StandaloneSection>
    );
  }

  if (!topZone) {
    return (
      <StandaloneSection title="CONFLUENCE ZONE">
        <div className="empty-state">No current confluence zones detected</div>
      </StandaloneSection>
    );
  }

  return (
    <StandaloneSection title="CONFLUENCE ZONE">
      <div className="confluence-zone-card">
        {isNearPrice(topZone) && (
          <div className="price-alert">
            ⚠️ Price is within 0.5% of this confluence zone!
          </div>
        )}

        <div className="zone-header">
          <div className="zone-strength">
            {getStrengthBadge(topZone)}
            <span className="level-count">{topZone.confluenceCount} levels converging</span>
          </div>
          <div className="zone-price">
            <span className="price-label">Center Price:</span>
            <span className="price-value">${topZone.centerPrice.toFixed(4)}</span>
          </div>
        </div>

        <div className="zone-stats">
          <div className="stat">
            <span className="stat-label">Distance from Current</span>
            <span className={`stat-value ${topZone.distanceFromCurrent >= 0 ? 'positive' : 'negative'}`}>
              {topZone.distanceFromCurrent >= 0 ? '+' : ''}${Math.abs(topZone.distanceFromCurrent).toFixed(4)}
              ({topZone.distancePct >= 0 ? '+' : ''}{topZone.distancePct.toFixed(2)}%)
            </span>
          </div>
          <div className="stat">
            <span className="stat-label">Timeframes</span>
            <span className="stat-value">{topZone.timeframeCount}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Price Range</span>
            <span className="stat-value">
              ${topZone.minPrice.toFixed(4)} - ${topZone.maxPrice.toFixed(4)}
            </span>
          </div>
        </div>

        <div className="converging-levels">
          <h4>Converging Levels</h4>
          <div className="levels-table">
            <div className="levels-header">
              <span className="header-timeframe">Timeframe</span>
              <span className="header-type">Level</span>
              <span className="header-price">Price</span>
              <span className="header-strength">Strength</span>
              <span className="header-interactions">History</span>
            </div>
            <div className="levels-list">
              {topZone.convergingLevels.map((level, index) => {
                const strengthPct = level.avgStrength * 100;
                const strengthClass =
                  strengthPct >= 80 ? 'very-strong' :
                  strengthPct >= 60 ? 'strong' :
                  strengthPct >= 40 ? 'moderate' : 'weak';

                return (
                  <div key={index} className="level-item">
                    <span className="level-timeframe">{level.timeframe}</span>
                    <span className="level-type">{level.levelType}</span>
                    <span className="level-price">${level.levelValue.toFixed(4)}</span>
                    <div className="level-strength" title={`Historical strength: ${strengthPct.toFixed(0)}%`}>
                      <div className="strength-bar">
                        <div
                          className={`strength-fill ${strengthClass}`}
                          style={{ width: `${strengthPct}%` }}
                        />
                      </div>
                      <span className="strength-pct">{strengthPct.toFixed(0)}%</span>
                    </div>
                    {level.interactionCount > 0 && (
                      <span className="interaction-count" title={`${level.bounceCount} bounces / ${level.interactionCount} total interactions`}>
                        {level.interactionCount}x
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </StandaloneSection>
  );
}
